import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import AccountAPI from '../../api/account';
import { differenceInDays } from 'date-fns';
import EnterpriseAccountAPI from '../../api/enterprise/account';
import { throwErrorMessage } from '../utils/api';
import { getLanguageDirection } from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';

const findRecordById = ($state, id) => {
  const normalizedId = Number(id);
  return $state.records.find(record => Number(record.id) === normalizedId) || {};
};

const TRIAL_PERIOD_DAYS = 15;

const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isUpdating: false,
    isCheckoutInProcess: false,
    isFetchingLimits: false,
  },
};

export const getters = {
  getAccount: $state => id => {
    return findRecordById($state, Number(id));
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  isRTL: ($state, _getters, rootState, rootGetters) => {
    const accountId = Number(rootState.route?.params?.accountId);
    const userLocale = rootGetters?.getUISettings?.locale;
    const accountLocale =
      accountId && findRecordById($state, accountId)?.locale;

    // Prefer user locale; fallback to account locale
    const effectiveLocale = userLocale ?? accountLocale;

    return effectiveLocale ? getLanguageDirection(effectiveLocale) : false;
  },
  isTrialAccount: $state => id => {
    const account = findRecordById($state, id);
    const createdAt = new Date(account.created_at);
    const diffDays = differenceInDays(new Date(), createdAt);

    return diffDays <= TRIAL_PERIOD_DAYS;
  },
  isFeatureEnabledonAccount: $state => (id, featureName) => {
    const { features = {} } = findRecordById($state, id);
    return features[featureName] || false;
  },
};

export const actions = {
  get: async ({ commit }) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isFetchingItem: true });
    try {
      const response = await AccountAPI.get();
      commit(types.default.ADD_ACCOUNT, response.data);
      commit(types.default.SET_ACCOUNT_UI_FLAG, {
        isFetchingItem: false,
      });
    } catch (error) {
      commit(types.default.SET_ACCOUNT_UI_FLAG, {
        isFetchingItem: false,
      });
    }
  },
  update: async ({ commit }, { options, ...updateObj }) => {
    if (options?.silent !== true) {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: true });
    }

    try {
      const response = await AccountAPI.update('', updateObj);
      commit(types.default.EDIT_ACCOUNT, response.data);
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: false });
      throw new Error(error);
    }
  },
  delete: async ({ commit }, { id }) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: true });
    try {
      await AccountAPI.delete(id);
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: false });
      throw new Error(error);
    }
  },
  toggleDeletion: async (
    { commit },
    { action_type } = { action_type: 'delete' }
  ) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: true });
    try {
      await EnterpriseAccountAPI.toggleDeletion(action_type);
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: false });
      throw new Error(error);
    } finally {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: false });
    }
  },
  create: async ({ commit }, accountInfo) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isCreating: true });
    try {
      const response = await AccountAPI.createAccount(accountInfo);
      const account_id = response.data.data.account_id;
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isCreating: false });
      return account_id;
    } catch (error) {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isCreating: false });
      throw error;
    }
  },

  checkout: async ({ commit }) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isCheckoutInProcess: true });
    try {
      const response = await EnterpriseAccountAPI.checkout();
      window.location = response.data.redirect_url;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isCheckoutInProcess: false });
    }
  },

  subscription: async ({ commit }) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isCheckoutInProcess: true });
    try {
      await EnterpriseAccountAPI.subscription();
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isCheckoutInProcess: false });
    }
  },

  limits: async ({ commit }) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isFetchingLimits: true });
    try {
      const response = await EnterpriseAccountAPI.getLimits();
      commit(types.default.SET_ACCOUNT_LIMITS, response.data);
    } catch (error) {
      // silent error
    } finally {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isFetchingLimits: false });
    }
  },

  getCacheKeys: async () => {
    return AccountAPI.getCacheKeys();
  },
};

export const mutations = {
  [types.default.SET_ACCOUNT_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.default.ADD_ACCOUNT]($state, data) {
    // Normalize id to Number
    const normalizedId = Number(data.id);
    const normalizedData = {
      ...data,
      id: normalizedId,
    };

    // Use Number comparison for findIndex
    const recordIndex = $state.records.findIndex(
      record => Number(record.id) === normalizedId
    );

    if (recordIndex > -1) {
      const existing = $state.records[recordIndex];

      // Preserve existing features if payload doesn't have features or features is empty
      const mergedData = {
        ...existing,
        ...normalizedData,
        // Only overwrite features if payload has features and it's not empty
        features:
          data.features && Object.keys(data.features).length > 0
            ? data.features
            : existing.features || {},
      };

      $state.records[recordIndex] = mergedData;
    } else {
      MutationHelpers.create($state, normalizedData);
    }
  },
  [types.default.EDIT_ACCOUNT]($state, data) {
    // Normalize id to Number
    const normalizedId = Number(data.id);
    const normalizedData = {
      ...data,
      id: normalizedId,
    };

    $state.records.forEach((element, index) => {
      if (Number(element.id) === normalizedId) {
        const existingFeatures = element.features || {};
        const payloadFeatures = normalizedData.features || {};
        const mergedFeatures =
          Object.keys(payloadFeatures).length > 0
            ? payloadFeatures
            : existingFeatures;

        $state.records[index] = {
          ...element,
          ...normalizedData,
          features: mergedFeatures,
        };
      }
    });
  },
  [types.default.SET_ACCOUNT_LIMITS]($state, data) {
    const normalizedId = Number(data.id);
    $state.records.forEach((element, index) => {
      if (Number(element.id) === normalizedId) {
        $state.records[index] = {
          ...$state.records[index],
          ...data,
          features: $state.records[index].features || {},
        };
      }
    });
  },
  [types.default.SET_ACCOUNTS]($state, data) {
    if (Array.isArray(data)) {
      const existingRecordsMap = new Map();
      $state.records.forEach(record => {
        const normalizedId = Number(record.id);
        existingRecordsMap.set(normalizedId, record);
      });
      const mergedRecords = data.map(newRecord => {
        const normalizedId = Number(newRecord.id);
        const existing = existingRecordsMap.get(normalizedId);

        if (existing) {
          const newFeatures = newRecord.features || {};
          const existingFeatures = existing.features || {};
          const mergedFeatures =
            Object.keys(newFeatures).length > 0 ? newFeatures : existingFeatures;

          return {
            ...existing,
            ...newRecord,
            id: normalizedId,
            features: mergedFeatures,
          };
        }

        return {
          ...newRecord,
          id: normalizedId,
        };
      });

      $state.records = mergedRecords;
    } else {
      $state.records = Array.isArray(data) ? data : [];
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
